import { Box, Modal, CircularProgress, Alert } from "@mui/material";
import { useMyContext } from "../../context";
import { modalStyle } from "../../MUIStyles";
import DialogsButtons from "../DialogsButtons";
import useRoles from "../../hooks/useRoles";
import { useEffect, useState, useRef } from "react";

const AddEditRole = () => {
  const { modalStates, handleCloseModal, roleData, setRoleData, id, setId } =
    useMyContext();
  const {
    permissionData,
    permissionError,
    permissionIsError,
    permissionLoading,
    addUpdateRoleLoading,
    roleDetails,
    roleDetailsError,
    roleDetailsIsError,
    roleDetailsLoading,
    handleAddUpdateRole,
  } = useRoles();
  const [fieldsChanged, setFieldsChanged] = useState(false);

  const isEditMode = !!id;

  // Store initial data for comparison - use ref to avoid re-renders
  const initialDataRef = useRef({
    name: "",
    note: "",
    permissions: [],
  });

  // Track if data has changed
  const [hasChanges, setHasChanges] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRoleData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePermissionChange = (permissionId) => {
    setRoleData((prev) => {
      const currentPermissions = prev.permissions || [];

      // Check if permission is already selected
      if (currentPermissions.includes(permissionId)) {
        // Remove permission
        return {
          ...prev,
          permissions: currentPermissions.filter((id) => id !== permissionId),
        };
      } else {
        // Add permission
        return {
          ...prev,
          permissions: [...currentPermissions, permissionId],
        };
      }
    });
  };

  // Check if a permission is selected
  const isPermissionSelected = (permissionId) => {
    return roleData.permissions?.includes(permissionId) || false;
  };

  // Handle Select All checkbox
  const handleSelectAll = () => {
    const allPermissionIds = permissionData?.data?.map((p) => p.id) || [];

    if (areAllPermissionsSelected()) {
      // If all are selected, deselect all
      setRoleData((prev) => ({
        ...prev,
        permissions: [],
      }));
    } else {
      // If not all are selected, select all
      setRoleData((prev) => ({
        ...prev,
        permissions: allPermissionIds,
      }));
    }
  };

  // Check if all permissions are selected
  const areAllPermissionsSelected = () => {
    const allPermissionIds = permissionData?.data?.map((p) => p.id) || [];
    return (
      allPermissionIds.length > 0 &&
      allPermissionIds.every((id) => roleData.permissions?.includes(id))
    );
  };

  // Check if some permissions are selected (for indeterminate state)
  const areSomePermissionsSelected = () => {
    const allPermissionIds = permissionData?.data?.map((p) => p.id) || [];
    return (
      roleData.permissions?.length > 0 &&
      roleData.permissions?.length < allPermissionIds.length
    );
  };

  const checkForChanges = () => {
    const initial = initialDataRef.current;

    const nameChanged = roleData.name !== initial.name;
    const noteChanged = roleData.note !== initial.note;

    setFieldsChanged(nameChanged || noteChanged);
  };

  // Load role details when in edit mode and modal opens
  useEffect(() => {
    if (modalStates.addEditRole && isEditMode && roleDetails?.data) {
      const role = roleDetails.data;
      const rolePermissions = role.permissions?.map((p) => p.id) || [];

      const newRoleData = {
        name: role.name || "",
        note: role.note || "",
        permissions: rolePermissions,
      };

      // Set the initial data ref
      initialDataRef.current = {
        name: role.name || "",
        note: role.note || "",
        permissions: rolePermissions,
      };

      // Set the form data
      setRoleData(newRoleData);

      // Reset hasChanges for edit mode
      setHasChanges(false);
    }
  }, [modalStates.addEditRole, isEditMode, roleDetails?.data, setRoleData]);

  // Reset for add mode
  useEffect(() => {
    if (modalStates.addEditRole && !isEditMode) {
      // Clear form for add mode
      setRoleData({
        name: "",
        note: "",
        permissions: [],
      });
      initialDataRef.current = {
        name: "",
        note: "",
        permissions: [],
      };
      setHasChanges(false);
    }
  }, [modalStates.addEditRole, isEditMode, setRoleData]);

  useEffect(() => {
    if (modalStates.addEditRole) {
      checkForChanges();
    }
  }, [roleData, modalStates.addEditRole]);

  const closeModal = () => {
    handleCloseModal("addEditRole");
    // Reset everything when modal closes
    setRoleData({
      name: "",
      note: "",
      permissions: [],
    });
    initialDataRef.current = {
      name: "",
      note: "",
      permissions: [],
    };
    setHasChanges(false);
    setId("");
  };

  // Check if form has any data
  const hasFormData = () => {
    return (
      roleData.name?.trim() !== "" ||
      roleData.note?.trim() !== "" ||
      roleData.permissions?.length > 0
    );
  };

  const isFormValid = () => {
    const hasName = roleData.name?.trim() !== "";
    const hasNote = roleData.note?.trim() !== "";
    const hasPermissions = roleData.permissions?.length > 0;

    if (!hasName || !hasNote) return false;

    if (isEditMode) {
      // For edit mode, check if there are changes
      const hasChangesInData = fieldsChanged || hasPermissions;
      return hasChangesInData;
    }

    // For add mode, check if all required fields are filled
    return hasName && hasNote && hasPermissions;
  };

  // Check if button should be disabled
  const shouldDisableButton = () => {
    // Always disable if loading
    if (addUpdateRoleLoading) return true;

    // If no data at all in the form, disable
    if (!hasFormData()) return true;

    // If form is not valid, disable
    if (!isFormValid()) return true;

    return false;
  };

  // Loading State for permissions
  if (permissionLoading) {
    return (
      <Modal open={modalStates.addEditRole} onClose={closeModal}>
        <Box
          sx={{
            ...modalStyle,
            maxHeight: "90vh",
            width: { lg: "1200px", xs: "400px", sm: "600px" },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div className="flex flex-col items-center gap-4">
            <CircularProgress />
            <p className="text-gray-600">Loading permissions...</p>
          </div>
        </Box>
      </Modal>
    );
  }

  // Loading State for role details (only in edit mode when modal is open)
  if (modalStates.addEditRole && isEditMode && roleDetailsLoading) {
    return (
      <Modal open={modalStates.addEditRole} onClose={closeModal}>
        <Box
          sx={{
            ...modalStyle,
            maxHeight: "90vh",
            width: { lg: "1200px", xs: "400px", sm: "600px" },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div className="flex flex-col items-center gap-4">
            <CircularProgress />
            <p className="text-gray-600">Loading role details...</p>
          </div>
        </Box>
      </Modal>
    );
  }

  // Error State for permissions
  if (permissionIsError) {
    return (
      <Modal open={modalStates.addEditRole} onClose={closeModal}>
        <Box
          sx={{
            ...modalStyle,
            maxHeight: "90vh",
            width: { lg: "1200px", xs: "400px", sm: "600px" },
          }}
        >
          <div className="flex flex-col gap-6 items-center p-6">
            <Alert severity="error" className="w-full">
              Failed to load permissions:{" "}
              {permissionError?.message || "Unknown error occurred"}
            </Alert>
            <DialogsButtons
              color="secondary"
              Btn1="Close"
              Btn2="Retry"
              onClose={closeModal}
              onConfirm={() => window.location.reload()}
            />
          </div>
        </Box>
      </Modal>
    );
  }

  // Error State for role details (only in edit mode)
  if (modalStates.addEditRole && isEditMode && roleDetailsIsError) {
    return (
      <Modal open={modalStates.addEditRole} onClose={closeModal}>
        <Box
          sx={{
            ...modalStyle,
            maxHeight: "90vh",
            width: { lg: "1200px", xs: "400px", sm: "600px" },
          }}
        >
          <div className="flex flex-col gap-6 items-center p-6">
            <Alert severity="error" className="w-full">
              Failed to load role details:{" "}
              {roleDetailsError?.message || "Unknown error occurred"}
            </Alert>
            <DialogsButtons
              color="secondary"
              Btn1="Close"
              Btn2="Retry"
              onClose={closeModal}
              onConfirm={() => window.location.reload()}
            />
          </div>
        </Box>
      </Modal>
    );
  }

  // Main Component
  return (
    <Modal open={modalStates.addEditRole} onClose={closeModal}>
      <Box
        sx={{
          ...modalStyle,
          maxHeight: "90vh",
          overflowY: "auto",
          width: { lg: "1200px", xs: "400px", sm: "600px" },
        }}
      >
        <div className="flex flex-col gap-10 items-center font-semibold">
          <h3 className="text-xl">
            {isEditMode ? "Edit Role" : "Add New Role"}
          </h3>

          {/* Role Information */}
          <div className="w-full flex items-center lg:flex-row flex-col gap-5 justify-around">
            <div className="lg:w-[40%] w-full flex flex-col gap-3">
              <label htmlFor="name">Role Name:</label>
              <input
                name="name"
                type="text"
                className="input-style"
                placeholder="Enter role name"
                value={roleData.name || ""}
                onChange={handleInputChange}
              />
              {!roleData.name && (
                <span className="text-red-500 text-xs">
                  Role name is required
                </span>
              )}
            </div>
            <div className="lg:w-[40%] w-full flex flex-col gap-3">
              <label htmlFor="note">Role Description:</label>
              <input
                name="note"
                type="text"
                className="input-style"
                placeholder="Enter role description"
                value={roleData.note || ""}
                onChange={handleInputChange}
              />
              {!roleData.note && (
                <span className="text-red-500 text-xs">
                  Description is required
                </span>
              )}
            </div>
          </div>

          {/* Permissions Section */}
          <div className="w-full">
            <div className="flex justify-between items-center mb-4">
              <h3>Permissions:</h3>
              {/* Select All checkbox */}
              {permissionData?.data?.length > 0 && (
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <input
                      type="checkbox"
                      id="select-all-permissions"
                      checked={areAllPermissionsSelected()}
                      onChange={handleSelectAll}
                      ref={(input) => {
                        if (input) {
                          input.indeterminate = areSomePermissionsSelected();
                        }
                      }}
                      className="
                        w-5 h-5 
                        appearance-none 
                        border-2 border-gray-400 
                        rounded 
                        checked:bg-secondary 
                        checked:border-secondary
                        indeterminate:bg-secondary/50
                        indeterminate:border-secondary
                        cursor-pointer
                        focus:ring-2 focus:ring-secondary/50
                      "
                    />
                    {(areAllPermissionsSelected() ||
                      areSomePermissionsSelected()) && (
                      <img
                        src="/img/checked.svg"
                        alt="Checked"
                        className="
                          absolute top-1 left-0.5 
                          w-4 h-3
                          pointer-events-none
                          ${areSomePermissionsSelected() ? 'opacity-50' : ''}
                        "
                      />
                    )}
                  </div>
                  <label
                    htmlFor="select-all-permissions"
                    className="font-medium cursor-pointer text-sm"
                  >
                    Select All
                  </label>
                </div>
              )}
            </div>

            <p className="text-gray-500 text-xs mb-4">
              Select permissions for this role
            </p>

            {/* Selected Permissions Count */}
            {roleData.permissions?.length > 0 && (
              <div className="mb-4 p-2 bg-blue-50 rounded">
                <p className="text-sm font-medium">
                  Selected {roleData.permissions.length} permission
                  {roleData.permissions.length !== 1 ? "s" : ""}
                  {areAllPermissionsSelected() && " (All)"}
                </p>
              </div>
            )}

            {/* Permissions Grid */}
            {permissionData?.data?.length === 0 ? (
              <Alert severity="info" className="w-full">
                No permissions available. Please add permissions first.
              </Alert>
            ) : (
              <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-2 w-full">
                {permissionData?.data?.map((permission) => (
                  <div key={permission?.id} className="flex items-center gap-3">
                    <div className="relative">
                      <input
                        type="checkbox"
                        id={`permission-${permission?.id}`}
                        checked={isPermissionSelected(permission.id)}
                        onChange={() => handlePermissionChange(permission.id)}
                        className="
                          w-5 h-5 
                          appearance-none 
                          border-2 border-gray-400 
                          rounded 
                          checked:bg-secondary 
                          checked:border-secondary
                          cursor-pointer
                          focus:ring-2 focus:ring-secondary/50
                        "
                      />
                      {isPermissionSelected(permission.id) && (
                        <img
                          src="/img/checked.svg"
                          alt="Checked"
                          className="
                            absolute top-1 left-0.5 
                            w-4 h-3
                            pointer-events-none
                          "
                        />
                      )}
                    </div>
                    <label
                      htmlFor={`permission-${permission?.id}`}
                      className="font-medium cursor-pointer text-sm flex-1"
                    >
                      {permission?.name}
                    </label>
                  </div>
                ))}
              </div>
            )}

            {/* No Permissions Warning */}
            {roleData.permissions?.length === 0 && (
              <Alert severity="warning" className="w-full mt-4">
                Please select at least one permission
              </Alert>
            )}
          </div>

          {/* Action Buttons */}
          <DialogsButtons
            color="secondary"
            Btn1="Close"
            Btn2={
              addUpdateRoleLoading
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                  ? "Update Role"
                  : "Create Role"
            }
            onClose={closeModal}
            onClick={handleAddUpdateRole}
            disabled={shouldDisableButton()}
          />
        </div>
      </Box>
    </Modal>
  );
};

export default AddEditRole;
